import { Stack, styled, Typography } from "@mui/material"
import { Skeleton } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { ArrowNarrowRight } from "@untitled-ui/icons-react"

import { PoolDetails, TokenDetails } from "./SwapMultiHopRoute"
import { DcaOrderFormValues } from "./types/DcaOrderForm"
import { LimitOrderFormValues } from "./types/LimitOrderForm"
import { StopLosOrderFormValues } from "./types/StopLossOrderForm"
import { OrderType } from "./types/swap-types"
import { getDcaParams } from "../../ao/agents-api"
import { useAo } from "../../ao/ao"
import { TokenInfo } from "../../ao/token"
import { NumberFont, TitleFont } from "../../theme"
import { formatDuration, formatNumberAuto, formatTicker } from "../../utils/format"
import { BeeLetter } from "../BeeLetter"
import TokenLogo from "../TokenLogo"
import { TokenPill } from "../TokenPill"

type SwapOrderPendingFormProps = {
  fromToken: TokenInfo
  toToken: TokenInfo
  fromValue: string
  toValue: string
  orderType: OrderType
  orderExtra: LimitOrderFormValues | DcaOrderFormValues | StopLosOrderFormValues | null
  multiHop?: {
    pool_addresses: string[]
    distance: number
    path: string[]
    amount_out: string
  }
}

export function SwapOrderPendingForm(props: SwapOrderPendingFormProps) {
  const { orderType, orderExtra, fromValue, toValue, fromToken, toToken, multiHop } = props

  const hasOutput =
    orderType === "swap" ||
    orderType === "limit" ||
    (orderType === "stop-loss" && (orderExtra as StopLosOrderFormValues)?.variant === "limit")

  const ao = useAo()

  const { data: dcaParams, isLoading } = useQuery({
    queryKey: ["dcaParams", orderExtra],
    queryFn: async () => {
      const result = await getDcaParams((orderExtra as DcaOrderFormValues)?.runningTime, ao)
      console.log("onDcaParams", result)
      return result
    },
    enabled: orderType === "dca",
  })

  return (
    <Stack gap={1} alignItems="center">
      <BeeLetter />
      <Typography variant="h5" fontFamily={TitleFont}>
        {multiHop ? "The Agent is processing the transaction" : "Pending Transaction"}
      </Typography>
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        sx={{
          width: "100%",
          "& > *": {
            flex: 1,
            flexShrink: 0,
            overflow: "hidden",
          },
          "& > *:nth-child(1)": {
            flexBasis: "calc(50% - 12px - var(--spacing))",
            alignItems: "flex-end",
          },
          "& > *:nth-child(2)": {
            flexBasis: "24px",
          },
          "& > *:nth-child(3)": {
            flexBasis: "calc(50% - 12px - var(--spacing))",
          },
        }}
      >
        <Stack>
          <TokenPill variant="outlined">
            <TokenLogo tx={fromToken.logo} alt={`${fromToken.name} logo`} size="small" />
            <Typography
              fontFamily={NumberFont}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              {formatNumberAuto(fromValue)}
            </Typography>
            {formatTicker(fromToken.ticker)}
          </TokenPill>
        </Stack>
        <ArrowNarrowRight />
        <Stack>
          <TokenPill variant="outlined">
            <TokenLogo tx={toToken.logo} alt={`${toToken.name} logo`} size="small" />
            {hasOutput && (
              <Typography
                fontFamily={NumberFont}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {formatNumberAuto(toValue)}
              </Typography>
            )}
            {formatTicker(toToken.ticker)}
            {!hasOutput && orderType !== "dca" && <> at market price</>}
          </TokenPill>
        </Stack>
      </Stack>
      {orderType === "dca" && (
        <>
          Dollar Cost Averaging {isLoading ? <Skeleton width={60} /> : dcaParams?.totalTicks} times,
          every{" "}
          {isLoading ? <Skeleton width={60} /> : formatDuration(dcaParams?.tickInterval, "seconds")}
          .
        </>
      )}
      {multiHop && (
        <Stack direction="column" gap={1} mt={2}>
          <DetailsRow>
            <Stack direction="row" gap={1}>
              {multiHop.pool_addresses.map((pool) => (
                <>
                  <PoolDetails key={pool} pool={pool} />
                  {multiHop &&
                    pool !== multiHop.pool_addresses[multiHop.pool_addresses.length - 1] && (
                      <span>→</span>
                    )}
                </>
              ))}
            </Stack>
          </DetailsRow>
          <DetailsRow>
            <Stack direction="row" gap={1}>
              {multiHop.path.map((path) => (
                <>
                  <TokenDetails key={path} token={path} />
                  {multiHop && path !== multiHop.path[multiHop.path.length - 1] && <span>→</span>}
                </>
              ))}
            </Stack>
          </DetailsRow>
        </Stack>
      )}
    </Stack>
  )
}

const DetailsRow = styled(Stack)`
  flex-direction: row;
  justify-content: space-between;
  gap: var(--spacing);

  & > span:first-of-type {
    white-space: nowrap;
    color: var(--mui-palette-secondary-main);
  }

  & > span:last-of-type {
    font-family: ${NumberFont};
    text-align: right;
  }

  & > div:last-of-type {
    font-family: ${NumberFont};
    text-align: right;
  }
`
